import { zodResolver } from '@hookform/resolvers/zod';
import { CatalogItem } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  SimpleSelect,
} from '@kalos/ui';
import { type ColumnDef, getCoreRowModel } from '@tanstack/react-table';
import { useReactTable } from '@tanstack/react-table';
import { debounce } from 'lodash';
import { Minus, Pencil, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useGetLoadingColumns } from '../../../components/utils';
import { FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME, ROWS_PER_PAGE } from '../../../constants';
import { useToolFundCatalogQuery } from '../../../hooks/react-query/useToolFundCatalogQuery';
import { useMediaQuery } from '../../../hooks/useMedia';
import { CatalogItemFormDialog } from './CatalogItemFormDialog';
import {
  catalogSearchDefaultValues,
  catalogSearchSchema,
  type CatalogSearchValues,
  exportToolCatalogCsv,
  useToolFundCatalogRequest,
} from './useToolFundCatalogSearch';

const searchSchema = catalogSearchSchema;

type SearchSchema = CatalogSearchValues;

const staticCatalogItems = Array.from({ length: 5 }, (_, idx) => CatalogItem.create({ id: idx }));

interface ToolFundCatalogProps {
  catalogItemId?: number;
  // Admin management (add/edit tools and their trades)
  enableManagement?: boolean;
  // Selection mode props
  enableSelection?: boolean;
  selectedItems?: number[];
  onSelectionChange?: (selectedItems: number[]) => void;
  // Department filtering
  departmentId?: number;
  // Quantity management
  selectedQuantities?: Record<number, number>;
  onQuantityChange?: (itemId: number, quantity: number) => void;
}

export const ToolFundCatalog = ({
  catalogItemId,
  enableManagement = false,
  enableSelection = false,
  selectedItems = [],
  onSelectionChange,
  departmentId,
  selectedQuantities = {},
  onQuantityChange,
}: ToolFundCatalogProps) => {
  const isDetailView = catalogItemId && Number(catalogItemId) > 0;
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<CatalogItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const form = useForm<SearchSchema>({
    resolver: zodResolver(searchSchema),
    defaultValues: catalogSearchDefaultValues,
    mode: 'onChange',
  });

  const [searchValues, setSearchValues] = useState<SearchSchema>(form.getValues());

  useEffect(() => {
    const subscription = form.watch(
      debounce((value) => {
        setSearchValues((prev) => ({ ...prev, ...value }));
        setPage(0);
      }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME),
    );
    return () => subscription.unsubscribe();
  }, [form]);

  const { request, categories, tradeTypes, categoryOptions } = useToolFundCatalogRequest({
    searchValues,
    page,
    catalogItemId,
    departmentId,
  });

  const toolFundCatalogQuery = useToolFundCatalogQuery({
    filter: request,
  });

  useEffect(() => {
    if (toolFundCatalogQuery.isSuccess) {
      setTotalCount(toolFundCatalogQuery.data.totalCount);
    }
  }, [toolFundCatalogQuery.isSuccess, toolFundCatalogQuery.data]);

  const pageCount = totalCount ? Math.ceil(totalCount / ROWS_PER_PAGE) : 0;

  const isMobile = useMediaQuery('(max-width: 768px)');

  // Selection handlers
  const handleSelectItem = useCallback(
    (itemId: number, checked: boolean) => {
      if (!onSelectionChange) return;

      if (checked) {
        onSelectionChange([...selectedItems, itemId]);
      } else {
        onSelectionChange(selectedItems.filter((id) => id !== itemId));
      }
    },
    [onSelectionChange, selectedItems],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;

      const catalogItems = toolFundCatalogQuery.data?.results || [];
      if (checked) {
        const allIds = catalogItems.map((item) => item.id);
        onSelectionChange([...new Set([...selectedItems, ...allIds])]);
      } else {
        const currentPageIds = catalogItems.map((item) => item.id);
        onSelectionChange(selectedItems.filter((id) => !currentPageIds.includes(id)));
      }
    },
    [onSelectionChange, selectedItems, toolFundCatalogQuery.data?.results],
  );

  const handleQuantityChange = useCallback(
    (itemId: number, quantity: number) => {
      if (onQuantityChange) {
        onQuantityChange(itemId, quantity);
      }
    },
    [onQuantityChange],
  );

  const catalogItems = toolFundCatalogQuery.data?.results || [];
  const isAllSelected =
    catalogItems.length > 0 && catalogItems.every((item) => selectedItems.includes(item.id));

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportToolCatalogCsv({ request, tradeTypes, categories });
    } finally {
      setIsExporting(false);
    }
  }, [request, tradeTypes, categories]);

  const columns: ColumnDef<CatalogItem>[] = useMemo(
    () => [
      // Selection column (only when selection is enabled)
      ...(enableSelection
        ? [
            {
              id: 'select',
              header: () => (
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all items"
                />
              ),
              cell: ({ row }: { row: { original: CatalogItem } }) => (
                <Checkbox
                  checked={selectedItems.includes(row.original.id)}
                  onCheckedChange={(checked) => handleSelectItem(row.original.id, !!checked)}
                  aria-label={`Select ${row.original.name}`}
                />
              ),
              enableSorting: false,
              enableHiding: false,
            },
          ]
        : []),
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
        meta: {
          className: 'w-32 whitespace-normal break-words',
        },
        cell: ({ row, column }) => (
          <div className={column.columnDef.meta?.className} title={row.original.name}>
            {row.original.name}
          </div>
        ),
      },
      {
        meta: {
          className: 'w-12',
        },
        accessorKey: 'externalLink',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Link" />,
        cell: ({ row, column }) => (
          <div className={column.columnDef.meta?.className}>
            {row.original.externalLink && (
              <Button className="px-0" variant="link" asChild>
                <a href={row.original.externalLink} target="_blank" rel="noopener noreferrer">
                  View
                </a>
              </Button>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'brand',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Brand" />,
        meta: {
          className: 'w-32 truncate',
        },
        cell: ({ row, column }) => (
          <div className={column.columnDef.meta?.className} title={row.original.brand}>
            {row.original.brand}
          </div>
        ),
      },
      {
        accessorKey: 'price',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Price" />,
        meta: {
          className: 'w-16',
        },
        cell: ({ row, column }) => (
          <div className={column.columnDef.meta?.className}>${row.original.cost.toFixed(2)}</div>
        ),
      },
      ...(!isMobile
        ? ([
            {
              accessorKey: 'description',
              meta: {
                className: 'w-32',
              },
              header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
              cell: ({ row, column }) => (
                <div className={column.columnDef.meta?.className} title={row.original.description}>
                  {row.original.description}
                </div>
              ),
            },
            // Show quantity column when selection is enabled, otherwise show trades column
            ...(enableSelection
              ? [
                  {
                    meta: {
                      className: 'w-24',
                    },
                    accessorKey: 'quantity',
                    header: ({ column }: { column: any }) => (
                      <DataTableColumnHeader className="w-24" column={column} title="Quantity" />
                    ),
                    cell: ({ row, column }: { row: { original: CatalogItem }; column: any }) => {
                      const itemId = row.original.id;
                      const isSelected = selectedItems.includes(itemId);
                      const currentQuantity = isSelected ? selectedQuantities[itemId] || 1 : 0;

                      return (
                        <div className={column.columnDef.meta?.className}>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (currentQuantity <= 1) {
                                  // If quantity is 1 or less, set to 0 which will uncheck the item
                                  handleQuantityChange(itemId, 0);
                                } else {
                                  // Otherwise, decrease by 1
                                  handleQuantityChange(itemId, currentQuantity - 1);
                                }
                              }}
                              disabled={currentQuantity <= 0}
                              className="h-6 w-6 p-0"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="min-w-[20px] text-center font-semibold text-gray-900">
                              {currentQuantity}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                handleQuantityChange(itemId, currentQuantity + 1);
                              }}
                              className="h-6 w-6 p-0"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    },
                  },
                ]
              : [
                  {
                    meta: {
                      className: 'flex w-32 flex-wrap gap-1',
                    },
                    accessorKey: 'tradeTypeNameList',
                    header: ({ column }: { column: any }) => (
                      <DataTableColumnHeader className="w-32" column={column} title="Trades" />
                    ),
                    cell: ({ row, column }: { row: { original: CatalogItem }; column: any }) => (
                      <div className={column.columnDef.meta?.className}>
                        {row.original.tradeTypeNameList
                          .split(',')
                          .filter(Boolean)
                          .map((trade) => (
                            <Badge key={trade} variant="outline" className="text-xs">
                              {trade}
                            </Badge>
                          ))}
                      </div>
                    ),
                  },
                ]),
          ] satisfies ColumnDef<CatalogItem>[])
        : []),
      ...(enableManagement
        ? [
            {
              id: 'actions',
              meta: {
                className: 'w-12',
              },
              header: () => null,
              cell: ({ row }: { row: { original: CatalogItem } }) => (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Edit ${row.original.name}`}
                  onClick={() => {
                    setEditItem(row.original);
                    setFormOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              ),
            },
          ]
        : []),
    ],
    [
      isMobile,
      enableManagement,
      enableSelection,
      selectedItems,
      isAllSelected,
      handleSelectAll,
      handleSelectItem,
      selectedQuantities,
      handleQuantityChange,
    ],
  );

  const loadingColumns = useGetLoadingColumns(columns);

  const table = useReactTable<CatalogItem>({
    data: toolFundCatalogQuery.isPending
      ? staticCatalogItems
      : toolFundCatalogQuery.data?.results ?? [],
    columns: toolFundCatalogQuery.isPending ? loadingColumns : columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    enableSorting: false,
  });

  return (
    <>
      {enableSelection && (
        <div className="flex items-center justify-between px-4 py-2">
          <p className="text-muted-foreground text-sm">{selectedItems.length} item(s) selected</p>
          <Button type="button" variant="outline" size="sm" onClick={() => onSelectionChange?.([])}>
            Clear Selection
          </Button>
        </div>
      )}

      <Form {...form}>
        <div className="space-y-4">
          {!isDetailView && (
            <div className="px-4">
              <div className="grid max-w-full grid-cols-2 gap-4 lg:grid-cols-3">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Search by name..." {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="externalLink"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Link</FormLabel>
                      <FormControl>
                        <Input placeholder="Search by link..." {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="brand"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Brand</FormLabel>
                      <FormControl>
                        <Input placeholder="Search by brand..." {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <SimpleSelect
                        onChange={(value) => {
                          field.onChange(value ? Number(value) : undefined);
                        }}
                        values={categoryOptions}
                        selectedValue={field.value?.toString() ?? ''}
                        placeholder="Filter by category"
                      />
                    </FormItem>
                  )}
                />
                <Button
                  type="reset"
                  className="mt-auto max-w-max"
                  variant="outline"
                  onClick={() => {
                    form.reset(form.formState.defaultValues);
                    setPage(0);
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          )}

          {!isDetailView && (
            <div className="flex items-center px-4">
              {pageCount > 1 && (
                <DataTablePagination
                  className="max-w-full"
                  currentPage={page}
                  setPage={setPage}
                  pageCount={pageCount}
                />
              )}
              <p className="ml-auto text-sm">Total Entries: {totalCount}</p>
              {enableManagement && (
                <>
                  <Button
                    type="button"
                    className="ml-4"
                    variant="outline"
                    disabled={isExporting}
                    onClick={handleExport}
                  >
                    {isExporting ? 'Exporting...' : 'Export CSV'}
                  </Button>
                  <Button
                    type="button"
                    className="ml-2"
                    onClick={() => {
                      setEditItem(null);
                      setFormOpen(true);
                    }}
                  >
                    Add Tool
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </Form>
      <div className="max-w-full overflow-x-auto rounded-md border">
        {!toolFundCatalogQuery.isPending && catalogItems.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center p-8">
            <p className="text-muted-foreground text-sm">No Results</p>
          </div>
        ) : (
          <DataTable table={table} />
        )}
      </div>
      {enableManagement && (
        <CatalogItemFormDialog open={formOpen} onOpenChange={setFormOpen} item={editItem} />
      )}
    </>
  );
};
